// EE_V2_RESET: serviço antigo preservado temporariamente, mas não deve ser chamado pelo novo fluxo EE v2.
import type { WritingCorrection } from "@/data/mockWritingCorrection";
import { mockWritingCorrection } from "@/data/mockWritingCorrection";
import { historyService } from "@/services/historyService";

export interface WritingCorrectionPayload {
  section: "Section A" | "Section B" | string;
  instruction: string;
  userText: string;
  targetNclc?: string | number;
  clientId?: string;
  historyId?: string;
  writingTasks?: any[];
  existingMetadata?: any;
}

export type CorrectionSource = "openai" | "gemini" | "mock" | "simulated" | "local" | "mock_fallback" | "frontend_fallback";

export interface WritingCorrectionResponse {
  success: boolean;
  correction: WritingCorrection;
  isFallback: boolean;
  source?: CorrectionSource;
  warning?: string;
  message?: string;
  code?: string;
  persistedHistoryRecord?: any;
}

export interface AIUsageResponse {
  success: boolean;
  usedToday: number;
  dailyLimit: number;
  remaining: number;
  totalCorrections?: number;
  lastCorrectionAt?: string | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "";
const BACKEND_URL = `${API_BASE_URL}/api/corrections/ee`;

/**
 * Consulta limites de uso e cotas de IA do cliente no backend.
 */
export async function fetchAIUsage(clientId: string): Promise<AIUsageResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/ai-usage/${clientId}`);
    if (!response.ok) {
      throw new Error(`Erro ao obter estatísticas: ${response.status}`);
    }
    const data = await response.json();
    return {
      success: true,
      usedToday: data.usedToday ?? 0,
      dailyLimit: data.dailyLimit ?? 10,
      remaining: data.remaining ?? 10,
      totalCorrections: data.totalCorrections ?? 0,
      lastCorrectionAt: data.lastCorrectionAt ?? null
    };
  } catch (err) {
    console.warn("Falha ao consultar limites de uso da IA (backend offline):", err);
    return {
      success: false,
      usedToday: 0,
      dailyLimit: 10,
      remaining: 10
    };
  }
}

function extractJsonFromAiText(text: string) {
  const cleaned = String(text || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  try {
    return JSON.parse(cleaned);
  } catch {}

  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");

  if (firstBrace >= 0 && lastBrace > firstBrace) {
    const possibleJson = cleaned.slice(firstBrace, lastBrace + 1);
    return JSON.parse(possibleJson);
  }

  throw new Error("Não foi possível extrair JSON da resposta da IA.");
}

function getWritingNclcFromScore(score: number) {
  if (score >= 393) return { nclc: "10+", cefr: "C1/C2" };
  if (score >= 371) return { nclc: "9", cefr: "C1" };
  if (score >= 349) return { nclc: "8", cefr: "B2" };
  if (score >= 310) return { nclc: "7", cefr: "B2" };
  if (score >= 271) return { nclc: "6", cefr: "B1" };
  if (score >= 226) return { nclc: "5", cefr: "B1" };
  if (score >= 181) return { nclc: "4", cefr: "A2" };
  return { nclc: "<4", cefr: "A1" };
}

function normalizeAiWritingCorrection(raw: any) {
  const score = Number(
    raw?.score ??
    raw?.totalScore ??
    raw?.estimatedScore ??
    raw?.pontuacao ??
    raw?.pontuacaoEstimada ??
    raw?.result?.score ??
    0
  ) || 0;

  const maxScore = Number(
    raw?.maxScore ??
    raw?.maximumScore ??
    raw?.max_score ??
    450
  ) || 450;

  const level = getWritingNclcFromScore(score);

  const nclc =
    raw?.nclc ??
    raw?.nclcLevel ??
    raw?.niveauNCLC ??
    raw?.result?.nclc ??
    level.nclc;

  const cefr =
    raw?.cefr ??
    raw?.cefrLevel ??
    raw?.cecrl ??
    raw?.result?.cefr ??
    level.cefr;

  const feedback =
    raw?.feedback ??
    raw?.generalFeedback ??
    raw?.overallFeedback ??
    raw?.overallComment ??
    raw?.summary ??
    raw?.feedbackGeral ??
    "";

  const improvementPoints =
    raw?.improvementPoints ??
    raw?.mainImprovements ??
    raw?.pointsToImprove ??
    raw?.principaisPontosDeMelhoria ??
    raw?.weaknesses ??
    [];

  const errors =
    raw?.errors ??
    raw?.identifiedErrors ??
    raw?.mistakes ??
    raw?.erros ??
    [];

  const suggestions =
    raw?.suggestions ??
    raw?.studySuggestions ??
    raw?.recommendations ??
    raw?.sugestoes ??
    [];

  const optimizedText =
    raw?.optimizedText ??
    raw?.improvedText ??
    raw?.redacaoOtimizada ??
    raw?.rewrittenText ??
    "";

  const criteria =
    raw?.criteria ??
    raw?.criteres ??
    raw?.rubric ??
    {};

  if (!score || score <= 0) {
    console.warn("[WRITING_AI_NORMALIZE_SCORE_MISSING]", raw);
  }

  return {
    ...raw,
    score,
    totalScore: score,
    maxScore,
    nclc,
    cefr,
    feedback,
    generalFeedback: feedback,
    improvementPoints: Array.isArray(improvementPoints) ? improvementPoints : [],
    errors: Array.isArray(errors) ? errors : [],
    suggestions: Array.isArray(suggestions) ? suggestions : [],
    optimizedText,
    criteria,
    source: "gemini",
    correctedAt: new Date().toISOString(),
  };
}

/**
 * Envia uma redação para correção no servidor Express seguro.
 * Se o servidor estiver offline, o serviço detecta e retorna o fallback local.
 */
export async function requestWritingCorrection(payload: WritingCorrectionPayload): Promise<WritingCorrectionResponse> {
  console.log("[WRITING_SERVICE_CORRECT_START]", {
    textLength: payload.userText?.length,
    section: payload.section,
  });

  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

  if (!apiKey) {
    console.warn("Chave VITE_GEMINI_API_KEY não encontrada no ambiente. Usando fallback.");
    throw new Error("Chave VITE_GEMINI_API_KEY não encontrada no ambiente.");
  }

  const prompt = `Você é um avaliador do teste TEF Canada (Expression Écrite).
Corrija a seguinte redação para a ${payload.section}.
Instrução: ${payload.instruction}
Texto do aluno: ${payload.userText}

IMPORTANTE:
Responda SOMENTE com JSON válido.
Não use markdown.
Não use bloco de código.
Não escreva explicações fora do JSON.

Formato obrigatório:
{
  "score": number,
  "maxScore": 450,
  "nclc": "string",
  "cefr": "string",
  "generalFeedback": "string",
  "improvementPoints": ["string"],
  "errors": [
    {
      "id": "string",
      "category": "string",
      "original": "string",
      "corrected": "string",
      "severity": "low|medium|high",
      "explanation": "string"
    }
  ],
  "suggestions": ["string"],
  "optimizedText": "string",
  "criteria": {
    "taskAchievement": {
      "label": "Adequação à tarefa",
      "score": number,
      "maxScore": 10,
      "comment": "string"
    },
    "coherence": {
      "label": "Coerência e organização",
      "score": number,
      "maxScore": 10,
      "comment": "string"
    },
    "grammar": {
      "label": "Gramática e sintaxe",
      "score": number,
      "maxScore": 10,
      "comment": "string"
    },
    "vocabulary": {
      "label": "Vocabulário e precisão lexical",
      "score": number,
      "maxScore": 10,
      "comment": "string"
    },
    "register": {
      "label": "Registro e naturalidade",
      "score": number,
      "maxScore": 10,
      "comment": "string"
    }
  }
}`;

  console.log("[WRITING_AI_FETCH_REQUEST]", {
    url: "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent",
    method: "POST",
    promptLength: prompt.length,
  });

  try {
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }]
      })
    });

    console.log("[WRITING_AI_FETCH_RESPONSE_STATUS]", {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
    });

    const responseText = await response.text();
    console.log("[WRITING_AI_RAW_RESPONSE_TEXT]", responseText.slice(0, 1000));

    if (!response.ok) {
      throw new Error(`Erro na API Gemini: ${response.status} ${response.statusText} - ${responseText.slice(0, 1000)}`);
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (error) {
      console.error("[WRITING_AI_RESPONSE_JSON_PARSE_ERROR]", error);
      throw new Error("A resposta da API Gemini não é JSON válido.");
    }

    console.log("[WRITING_AI_RESPONSE_JSON]", JSON.stringify(data, null, 2));

    const generatedText =
      data?.candidates?.[0]?.content?.parts
        ?.map((part: any) => part.text || "")
        .join("\n")
      || data?.candidates?.[0]?.content?.parts?.[0]?.text
      || data?.text
      || "";

    console.log("[WRITING_AI_GENERATED_TEXT]", generatedText.slice(0, 5000));

    if (!generatedText || generatedText.trim().length < 20) {
      throw new Error("A Gemini respondeu, mas não retornou texto de correção.");
    }

    let correction;
    try {
      correction = extractJsonFromAiText(generatedText);
    } catch (error) {
      console.error("[WRITING_AI_CORRECTION_PARSE_ERROR]", error);
      console.log("[WRITING_AI_UNPARSED_TEXT]", generatedText);
      throw error;
    }

    console.log("[WRITING_AI_PARSED_CORRECTION]", JSON.stringify(correction, null, 2));

    const normalized = normalizeAiWritingCorrection(correction);

    console.log("[WRITING_AI_NORMALIZED_CORRECTION]", JSON.stringify(normalized, null, 2));

    return {
      success: true,
      correction: normalized,
      isFallback: false,
      source: "gemini"
    };

  } catch (error: any) {
    console.error("[WRITING_AI_SERVICE_ERROR]", error);
    console.warn("[WRITING_AI_USING_FALLBACK]", error);

    // Fallback local: herda a estrutura estática do mock, mas injeta os dados reais do payload para ser fidedigno!
    const wordCount = payload.userText.trim().split(/\s+/).filter(w => w.length > 0).length;
    
    const enhancedLocalCorrection: WritingCorrection = {
      ...mockWritingCorrection,
      section: payload.section,
      wordCount: wordCount,
      createdAt: new Date().toISOString(),
      source: "frontend_fallback" as any // Force source for persistence
    };

    console.log("[EE_CORRECTION_SERVICE_RESULT] (Fallback)", JSON.stringify(enhancedLocalCorrection, null, 2));

    let savedRecord: any = null;

    if (payload.historyId) {
      try {
        console.log("[PERSIST_WRITING_CORRECTION_PAYLOAD] (Fallback)", JSON.stringify({
          historyId: payload.historyId,
          correctionResult: enhancedLocalCorrection,
          section: payload.section
        }, null, 2));

        savedRecord = await historyService.persistWritingCorrection({
          historyId: payload.historyId,
          correctionResult: enhancedLocalCorrection,
          writingTasks: payload.writingTasks,
          section: payload.section,
          competency: "EE",
          existingMetadata: payload.existingMetadata,
        });

        console.log("[PERSIST_WRITING_CORRECTION_SUCCESS] (Fallback)", JSON.stringify(savedRecord, null, 2));
      } catch (persistErr) {
        console.error("[EE_AUTO_SAVE_SERVICE_ERROR] (Fallback)", persistErr);
      }
    }

    return {
      success: true,
      correction: enhancedLocalCorrection,
      isFallback: true,
      source: "frontend_fallback",
      message: `Erro na correção IA: ${error.message || error}. Usando correção simulada local.`,
      persistedHistoryRecord: savedRecord
    };
  }
}
